import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/* A panel that rises from the bottom of the screen.

   The mobile counterpart of Popover. A popover is anchored under its trigger,
   which on a phone puts a 300-row voice list wherever the chip happened to be —
   usually the top third, the part of the screen a thumb cannot reach, and
   clipped by whatever the keyboard is covering. A sheet always opens in the
   same place, is dismissed by the same two gestures wherever it came from, and
   can be as tall as it needs without leaving the viewport.

   Portalled to <body> so it escapes the composer's stacking context: a sheet
   that renders inside .card inherits the card's overflow and its z-index, and a
   sheet opened from a scrolling list scrolls away with the list. */

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export function Sheet({ open, title, onClose, children }: Props) {
  /* How far the user has dragged it down, in px. Only ever set while a drag is
     in progress; released back to 0 or straight into onClose. */
  const [drag, setDrag] = useState(0);
  const start = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setDrag(0);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    /* The page behind must not scroll under the sheet — on iOS a touch that
       starts on the scrim and moves scrolls the panel, so the sheet closes onto
       a page that has silently jumped somewhere else. */
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  function onTouchStart(event: React.TouchEvent) {
    start.current = event.touches[0].clientY;
  }

  function onTouchMove(event: React.TouchEvent) {
    if (start.current === null) return;
    // Downward only: dragging up would lift the sheet off the bottom edge and
    // show the page through the gap.
    setDrag(Math.max(0, event.touches[0].clientY - start.current));
  }

  function onTouchEnd() {
    start.current = null;
    // A short pull is a mis-grab, not a dismissal; snap back rather than
    // closing a voice list somebody was about to scroll.
    if (drag > 90) onClose();
    else setDrag(0);
  }

  return createPortal(
    <div className="sheet-scrim" onMouseDown={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={drag ? { transform: `translateY(${drag}px)`, transition: "none" } : undefined}
        // The scrim closes on a press outside; without this every press inside
        // bubbles up to it and the sheet shuts on its own content.
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* The grab area is the header, not just the 36px bar: aiming for the
            bar itself is a precision task, and the title is dead space. */}
        <div
          className="sheet-head"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <span className="sheet-grip" aria-hidden="true"></span>
          <h2>{title}</h2>
          <button className="ghost sheet-close" onClick={onClose} aria-label="Close">
            Done
          </button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/* A sheet that is a list of things to do to one row — the phone's answer to six
   icon buttons crammed into a 390px feed item. Labelled, full width, and the
   destructive ones marked, which the icons never managed. */

export interface SheetAction {
  label: string;
  hint?: string;
  danger?: boolean;
  run: () => void;
}

export function ActionSheet({
  open,
  title,
  actions,
  onClose,
}: {
  open: boolean;
  title: string;
  actions: SheetAction[];
  onClose: () => void;
}) {
  return (
    <Sheet open={open} title={title} onClose={onClose}>
      <div className="action-list">
        {actions.map((action) => (
          <button
            key={action.label}
            className={"action-row" + (action.danger ? " danger" : "")}
            onClick={() => {
              // Close first: several of these open a confirm dialog, and a
              // scrim under a scrim is how you end up unable to dismiss either.
              onClose();
              action.run();
            }}
          >
            <span className="action-label">{action.label}</span>
            {action.hint && <span className="action-hint">{action.hint}</span>}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
